import { Component, Input, Output, EventEmitter, OnInit, ViewChild, CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { ToastrService } from "ngx-toastr";
import { IDropDownItem, IDropDown, ICountryDescription, CountryDashboardInformation } from "../../interfaces/idashboard";
import { IBusinessFunction } from "../../interfaces/ibusinessfunction";
import { CountryDashboardService } from "../../services/countrydashboard.service";
import { CountryDashboardDescriptionLatestUpdate } from "../../models/latestupdate";
import { LatestUpdateService } from "../../services/latestupdate.service";
import { FormGroup, FormControl, Validators } from "@angular/forms";
import { AppSettings } from "../../appsettings";
import { ILatestUpdateModal } from "../../interfaces/ilatestupdatemodal";
import { LatestUpdateModalComponent } from "../latestupdatemodal/latestupdatemodal.component";
import { IAppStore } from "../../iappstore";
import { Store } from "@ngrx/store";
import * as ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { CKEditorModule } from "@ckeditor/ckeditor5-angular";
import { CkEditorEnhancedDirective } from "../../directives/ck-editor-enhanced.directive";

/**
 * Dashboard keys component
 */
@Component({
    selector: "dashboardkeys",
    templateUrl: "./dashboardkeys.component.html",
    standalone: true,
    imports: [CommonModule, FormsModule, ReactiveFormsModule, CKEditorModule, CkEditorEnhancedDirective, LatestUpdateModalComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA]
})
export class DashboardKeysComponent implements OnInit {
    public Editor = ClassicEditor; // Assign ClassicEditor to the Editor property
    @Input()
    dropdown: IDropDown;
    @Input()
    countryId: number;
    @Input()
    dashboardInformation: CountryDashboardInformation;
    latestNewsMaxLength: number = AppSettings.maxCharacterLimit;
    updateText: string = "";
    config: any = {};
    dashboardKeysForm: FormGroup;
    selectedKeyItemId: number;
    keyDropDownItems: IDropDownItem[];
    textEditorConfig = {};
    @ViewChild(LatestUpdateModalComponent)
    private latestUpdateModalComponent: LatestUpdateModalComponent;
    @Output() onLatestUpdateUpdated = new EventEmitter();

    constructor(
        private toastr: ToastrService,
        private countryDashboardService: CountryDashboardService,
        private latestUpdateService: LatestUpdateService,
        private store: Store<IAppStore>) {
    }

    /**
     * Initialse the the dashboard keys component
     */
    ngOnInit(): void {
        this.config = AppSettings.ckeditorConfig;
        let hoverText: string = "";
        let id: number = null;
        let dashboardDropdownId: number = this.dropdown.id;
        let countryId: number = this.countryId;
        if (this.dropdown != null) {
            this.keyDropDownItems = this.dropdown.dropDownItems;
            this.selectedKeyItemId = this.getSelectedItemKeyId();
            let selectedItem: ICountryDescription = this.getCurrentSelectedDropDownItem(this.selectedKeyItemId);
            if (selectedItem != null) {
                id = selectedItem.id;
                hoverText = selectedItem.text;
                dashboardDropdownId = selectedItem.dashboardDropdownId;
            }
        }
        this.dashboardKeysForm = new FormGroup({
            id: new FormControl(id),
            text: new FormControl(hoverText),
            dashboardDropdownId: new FormControl(dashboardDropdownId, Validators.required),
            countryId: new FormControl(countryId)
        });
        this.setLatestUpdateContent();
    }

    /**
     * get the selected key item id
     */
    getSelectedItemKeyId(): number {
        let countryDashboardInformationItem: Array<ICountryDescription> =
            this.dropdown.countryDescriptions.filter(c => c.countryId === this.countryId);
        return countryDashboardInformationItem && countryDashboardInformationItem.length > 0
            ? countryDashboardInformationItem[0].dropDownItemId
            : null;
    }

    /**
     * Gets invoked on the key item selected
     * @param item
     */
    onKeyItemSelected(item: IDropDownItem): void {
        if (this.selectedKeyItemId === item.id) {
            this.selectedKeyItemId = null;
            (<FormControl>this.dashboardKeysForm.controls["text"]).setValue("", { onlySelf: true });
        } else {
            this.selectedKeyItemId = item.id;
        }
        this.setLatestUpdateContent();
        this.dashboardKeysForm.markAsDirty();
    }

    /**
     * Returns the current dropdown item selected
     * @param id
     */
    getCurrentSelectedDropDownItem(id: number): ICountryDescription {
        let countryDescription: Array<ICountryDescription> = this.dropdown.countryDescriptions.filter(
            (c: ICountryDescription) => c.countryId === this.countryId &&
            c.dropDownItemId === id &&
            c.dashboardDropdownId === this.dropdown.id);
        if (countryDescription != null && countryDescription.length > 0) {
            return countryDescription[countryDescription.length - 1];
        } else {
            return null;
        }
    }

    /**
     * Method gets called when save is clicked on the form
     * @param form the FormGroup object bein submitted of type ICountryDescription
     */
    saveClicked(): void {
        let form: ICountryDescription = this.dashboardKeysForm.value;
        form.dropDownItemId = this.selectedKeyItemId;
        if (form.id == null) {
            // insert
            this.countryDashboardService.createDashboardContent(form).subscribe((i: ICountryDescription) => {
                (<FormControl>this.dashboardKeysForm.controls["id"])
                    .setValue(i.id);
                form.id = i.id;
                this.updateDropdownsArray(form);
                this.toastr.success("Save successful!");
            });
        } else {
            // update
            this.countryDashboardService.saveDashboardContent(form).subscribe(() => {
                this.updateDropdownsArray(form);
                if (form.dropDownItemId === null) {
                    this.toastr.success("Dashboard information successfuly removed!");
                } else {
                    this.toastr.success("Save successful!");
                }
            });
        }
    }

    /**
     * Update the the country description drop down array
     * @param countryDescription the countryDescription object
     */
    updateDropdownsArray(countryDescription: ICountryDescription): void {
        let index: number = this.dropdown.countryDescriptions.findIndex(c => c.id === countryDescription.id);
        if (index === -1) {
            this.dropdown.countryDescriptions.push(countryDescription);
        } else {
            if (countryDescription.dropDownItemId == null) {
                // this was a delete
                this.dropdown.countryDescriptions.splice(index, 1);
                (<FormControl>this.dashboardKeysForm.controls["id"]).setValue(null);
            } else {
                // this was a normal update
                this.dropdown.countryDescriptions[index].text = countryDescription.text;
            }
        }
    }

    /**
     * Publishes latest update to server
     * @param countryDescription
     */
    publishContent(latestUpdateContent: ILatestUpdateModal): void {
        if (latestUpdateContent.latestUpdateAlert) {
            let businessFunction: IBusinessFunction = null;

            this.store.select(e => e.user.selectedBusinessFunction)
                .subscribe(e => businessFunction = e);

            const latestUpdate: CountryDashboardDescriptionLatestUpdate = new CountryDashboardDescriptionLatestUpdate(this.countryId,
                businessFunction.id,
                latestUpdateContent.description,
                this.dashboardInformation.countryCode,
                businessFunction.defaultRoute,
                latestUpdateContent.sendEmail);
            this.latestUpdateService.create(latestUpdate).subscribe(() => {
                this.toastr.success("Latest updated published");
            });
            this.onLatestUpdateUpdated.emit();
        }
    }

    /**
     * gets the breadcrumb for the dropdown selection
     */
    getBreadCrumb(): string {
        let dropDownLabel: string = "";
        if (this.dashboardInformation == null) {
            return null;
        }
        this.dashboardInformation.dropDownsToRender.forEach(dropDown => {
            let dropDownId: number = dropDown.selectedDropDownItem.id;
            let items: IDropDownItem[] = dropDown.dropDownItems.filter(i => i.id === dropDownId);
            items.forEach((i: IDropDownItem) => {
                if (dropDown.presentationTypeId === 1) {
                    dropDownLabel = dropDownLabel + " " + i.text;
                }
            });
        });

        let keyText: string = `${this.dashboardInformation.countryName} - ${dropDownLabel}`;
        if (this.dashboardInformation.items.length > 0 && this.dashboardInformation.selectedItemIndex >= 0) {
            keyText = `${keyText} - ${this.dashboardInformation.items[this.dashboardInformation.selectedItemIndex]
                .itemLabel}`;
        }

        // if string ends
        if (keyText.endsWith(" - ")) {
            keyText = keyText.substr(0, keyText.lastIndexOf(" - "));
        }
        return keyText;
    }

    /**
     * needed to determine whether a key item has been selected or not
     * therefore we know whether to allow modal to show
     * @param latestUpdateModal
     */
    showPublishModal(): void {
        if (this.selectedKeyItemId != null) {
            this.latestUpdateModalComponent.openModal();
        }
    }

    /**
     * Sets the latest update content for the text area
     */
    setLatestUpdateContent(): void {
        this.updateText = `A change was made to ${this.getBreadCrumb()}.`;
        this.updateText = this.updateText.substring(0, AppSettings.maxCharacterLimit);
    }

    /**
     * Get the key Item for the given id
     * @param id
     */
    getKeyItem(id: number): IDropDownItem {
        const dropdown: IDropDownItem[] = this.dropdown.dropDownItems.filter((c: IDropDownItem) => c.id === id);
        if (dropdown != null && dropdown.length > 0) {
            return dropdown[0];
        } else {
            return null;
        }
    }
}



